import { ToastrHelper, showToast } from '$lib/helpers/toastrHelper';
import { type BaseListResponse, type ErrorResponse, SortBy } from '$lib/models/types';
import { type CreateUserRequest, type GetUserResponse, type PutUserRequest, type UserPageState } from '$lib/models/user/types';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { extractErrorMessage } from '$lib/utils/error/errorUtils';
import type { State } from '@vincjo/datatables/remote';
import { writable } from 'svelte/store';

export const uiState = writable<UserPageState>('NoUserPage');

export function setUIState(newState: UserPageState) {
  uiState.set(newState);
}

export const userId = writable<number | null>();

export function setUserId(id: number) {
  userId.set(id);
}

export const searchStore = writable({ search: '' });

export function setSearch(value: string) {
  searchStore.set({ search: value });
}


export const searchByPermissionStore = writable({ checked: false });

export function setSearchByPermission(value: boolean) {
    searchByPermissionStore.set({ checked: value });
}

export const sortingStore = writable({ sortBy: 'Name', orderBy: 'desc' });

export function setSorting(sortBy: string, orderBy: string) {
  sortingStore.set({ sortBy: sortBy, orderBy: orderBy });
}

function createUserStore() {
  const type: string = 'User';
  const baseUrl: string = '/api/user';

  return {
    fetchById: async (id: number): Promise<GetUserResponse | ErrorResponse> => {
      const response = await fetchWithAuth(`${baseUrl}/${id}`, {
        method: 'GET',
      });

      if (response.status == 204) {
        const errorMessage = ToastrHelper.notFound(type);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      try {
        return await response.json();
      } catch (error) {
        const errorMessage = ToastrHelper.errorRetrieving(type);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }
    },
    fetch: async (clientId: number, state: State): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = `/api/client/${clientId}/user`;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() === 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;

      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {
            //if (filterBy === ColumnNames.CreateDate) {
            //  const date = new Date();

            //  if (value === DateFilters.LastSevenDays) {
            //    date.setDate(date.getDate() - 7);
            //  } else if (value === DateFilters.LastFourteenDays) {
            //    date.setDate(date.getDate() - 14);
            //  } else if (value === DateFilters.LastMonth) {
            //    date.setMonth(date.getMonth() - 1);
            //  } else if (value === DateFilters.LastQuarter) {
            //    date.setMonth(date.getMonth() - 3);
            //  } else if (value === DateFilters.LastTwelveMonths) {
            //    date.setFullYear(date.getFullYear() - 1);
            //  }

            //  return `&${filterBy}=${date.toISOString()}`;
            //}

            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch users (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to parse user data',
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
    count: async (clientId: number): Promise<number> => {
      const response = await fetchWithAuth(`/api/client/${clientId}/user-count`, {
        method: 'GET',
      });

      if (response.ok) {
        return await response.json();
      } else {
        return 0;
      }
    },
    create: async (user: CreateUserRequest): Promise<void> => {
      const response = await fetchWithAuth(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });

      if (!response.ok) {
        throw await response.json();
      }
    },
    update: async (request: PutUserRequest): Promise<void> => {
      const response = await fetchWithAuth(`${baseUrl}/${request.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        return;
      } else {
        throw await response.json();
      }
    },
    updateActiveStatus: async (id: number, isActive: boolean): Promise<void> => {
      const response = await fetchWithAuth(`${baseUrl}/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, isActive }),
      });

      if (!response.ok) {
        throw await response.json();
      }
    },
    getUserEmulationLink: async (id: number): Promise<void | ErrorResponse> => {
      const response = await fetchWithAuth(`${baseUrl}/${id}/emulate`, {
        method: 'GET',
      });

      if (response.ok) {
        const url = await response.text();
        const cleanedUrl  = url.trim().replace(/^"(.*)"$/, '$1');

        try {
          const parsedUrl = new URL(cleanedUrl);
          if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            showToast({
                title: 'Emulation Error',
                message: 'Invalid emulation URL received from server',
                type: 'error'
            });
            return;
          }
          window.open(cleanedUrl, '_blank');
        } catch {
          showToast({
            title: 'Emulation Error',
            message: 'Received an invalid emulation URL',
            type: 'error'
          });
        }
      } else {
        const errorMessage = await extractErrorMessage(response, `Failed to retrieve ${type} emulation token`);
        showToast({
          title: 'Emulation Error',
          message: errorMessage,
          type: 'error'
        });
      }
    },
    fetchPermissions: async (): Promise<Array<{ key: number; value: string }> | ErrorResponse> => {
      const response = await fetchWithAuth(`${baseUrl}/permissions`, {
        method: 'GET',
      });

      if (response.ok) {
        return await response.json();
      } else {
        return {
          message: ToastrHelper.errorRetrieving(`${type} permissions`),
          detail: ToastrHelper.errorRetrieving(`${type} permissions`), // Add detail field for Problem Details compatibility
        };
      }
    },
  };
}

export const userStore = createUserStore();
